// IBKR CSV Format Parsers
// Supports: Open Position Summary, Activity Statement, Trade Confirmation, Portfolio Analyst

export interface Position {
  ticker: string;
  description: string;
  sector: string;
  quantity: number;
  marketValue: number;
  costBasis: number;
  unrealizedPL: number;
  unrealizedPLPercent: number;
  currentPrice: number;
  avgCost: number;
  currency: string;
  assetClass?: string;
}

export interface Trade {
  date: string;
  ticker: string;
  description: string;
  action: 'BUY' | 'SELL';
  quantity: number;
  price: number;
  proceeds: number;
  commission: number;
  currency: string;
  realizedPL?: number;
}

export interface Dividend {
  date: string;
  ticker: string;
  description: string;
  amount: number;
  currency: string;
}

export interface CashTransaction {
  date: string;
  description: string;
  amount: number;
  currency: string;
  type: 'deposit' | 'withdrawal' | 'dividend' | 'interest' | 'fee' | 'other';
}

export interface AccountInfo {
  accountId: string;
  accountName: string;
  accountType: string;
  baseCurrency: string;
  statementPeriod: string;
}

export interface FinancialSummary {
  beginningNav: number;
  endingNav: number;
  deposits: number;
  withdrawals: number;
  dividendsReceived: number;
  interestReceived: number;
  feesAndCommissions: number;
  realizedPL: number;
  unrealizedPL: number;
  netChange: number;
}

export interface ParseResult {
  format: 'open_position_summary' | 'activity_statement' | 'trade_confirmation' | 'portfolio_analyst' | 'flex_query' | 'unknown';
  positions: Position[];
  trades: Trade[];
  dividends: Dividend[];
  cashTransactions: CashTransaction[];
  cashBalance: number;
  cashBalances: { currency: string; balance: number }[];
  currency: string;
  accountId?: string;
  accountInfo?: AccountInfo;
  statementPeriod?: string;
  financialSummary?: FinancialSummary;
}

// Detect the format of the IBKR CSV file
export function detectIBKRFormat(csvText: string): ParseResult['format'] {
  const lines = csvText.split('\n').slice(0, 100); // Check first 100 lines
  const headerText = lines.join('\n').toLowerCase();
  
  // Check for specific format markers
  if (headerText.includes('open position summary')) {
    return 'open_position_summary';
  }
  if (headerText.includes('statement,header') || 
      headerText.includes('account information') ||
      (headerText.includes('open positions') && headerText.includes('trades'))) {
    return 'activity_statement';
  }
  if (headerText.includes('trade confirmation') || headerText.includes('trades,header')) {
    return 'trade_confirmation';
  }
  if (headerText.includes('portfolio analyst') || headerText.includes('historical performance benchmark')) {
    return 'portfolio_analyst';
  }
  if (headerText.includes('flexqueryid') || headerText.includes('flex query')) {
    return 'flex_query';
  }
  
  return 'unknown';
}

// Parse Open Position Summary format (existing format)
export function parseOpenPositionSummary(csvText: string): ParseResult {
  const lines = csvText.split('\n');
  const positions: Position[] = [];
  let accountId = '';
  
  for (const line of lines) {
    const parts = line.split(',');
    
    // Get account ID
    if (parts[0] === 'Statement' && parts[1] === 'Header') {
      accountId = parts[4] || '';
    }
    
    if (parts[0] === 'Open Position Summary' && parts[1] === 'Data' && parts[3] !== 'Total') {
      const category = parts[3];
      const currency = parts[4]?.trim() || 'USD';
      const ticker = parts[5] || '';
      const description = parts[6] || '';
      const quantity = parseFloat(parts[8]) || 0;
      const marketValue = parseFloat(parts[10]) || 0;
      const costBasis = parseFloat(parts[11]) || 0;
      const unrealizedPL = parseFloat(parts[12]) || 0;

      if (ticker && category !== 'Cash') {
        positions.push({
          ticker,
          description,
          sector: parts[7] || '',
          quantity,
          marketValue,
          costBasis,
          unrealizedPL,
          unrealizedPLPercent: costBasis !== 0 ? (unrealizedPL / Math.abs(costBasis)) * 100 : 0,
          currentPrice: quantity !== 0 ? marketValue / quantity : 0,
          avgCost: quantity !== 0 ? costBasis / quantity : 0,
          currency,
          assetClass: category,
        });
      }
    }
  }

  return {
    format: 'open_position_summary',
    positions,
    trades: [],
    dividends: [],
    cashTransactions: [],
    cashBalance: 0,
    cashBalances: [],
    currency: 'USD',
    accountId,
  };
}

// Parse Activity Statement format - Enhanced for standard IBKR Activity Statements
export function parseActivityStatement(csvText: string): ParseResult {
  const lines = csvText.split('\n');
  const positions: Position[] = [];
  const trades: Trade[] = [];
  const dividends: Dividend[] = [];
  const cashTransactions: CashTransaction[] = [];
  const cashBalances: { currency: string; balance: number }[] = [];
  let cashBalance = 0;
  let currency = 'USD';
  let accountId = '';
  let statementPeriod = '';
  let accountInfo: AccountInfo | undefined;
  let financialSummary: FinancialSummary = {
    beginningNav: 0,
    endingNav: 0,
    deposits: 0,
    withdrawals: 0,
    dividendsReceived: 0,
    interestReceived: 0,
    feesAndCommissions: 0,
    realizedPL: 0,
    unrealizedPL: 0,
    netChange: 0,
  };
  
  let currentSection = '';
  let sectionHeaders: Map<string, number> = new Map();
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const parts = line.split(',').map(p => p.replace(/"/g, '').trim());
    
    // Detect section changes via Header rows
    if (parts[1] === 'Header') {
      currentSection = parts[0].toLowerCase();
      sectionHeaders = new Map();
      // Map header columns
      parts.forEach((col, idx) => {
        if (col && idx > 1) {
          sectionHeaders.set(col.toLowerCase(), idx);
        }
      });
      continue;
    }
    
    // Parse Statement Header for account info
    if (parts[0] === 'Statement' && parts[1] === 'Header') {
      accountId = parts[4] || '';
      statementPeriod = parts[5] || parts[6] || '';
    }
    
    // Parse Account Information section
    if (currentSection === 'account information' && parts[1] === 'Data') {
      const field = parts[2]?.toLowerCase() || '';
      const value = parts[3] || '';
      
      if (!accountInfo) {
        accountInfo = {
          accountId: accountId,
          accountName: '',
          accountType: '',
          baseCurrency: 'USD',
          statementPeriod: statementPeriod,
        };
      }
      
      if (field.includes('account')) accountInfo.accountId = value || accountInfo.accountId;
      if (field.includes('name')) accountInfo.accountName = value;
      if (field.includes('type')) accountInfo.accountType = value;
      if (field.includes('currency') || field.includes('base')) {
        accountInfo.baseCurrency = value || 'USD';
        currency = value || 'USD';
      }
    }
    
    // Parse Net Asset Value section
    if (currentSection === 'net asset value' && parts[1] === 'Data') {
      const curr = parts[2] || 'USD';
      const priorTotal = parseFloat(parts[3]) || 0;
      const currentLong = parseFloat(parts[4]) || 0;
      const currentShort = parseFloat(parts[5]) || 0;
      const currentTotal = parseFloat(parts[6]) || 0;
      const change = parseFloat(parts[7]) || 0;
      
      if (curr && curr !== 'Total') {
        if (financialSummary.beginningNav === 0) financialSummary.beginningNav = priorTotal;
        if (currentTotal !== 0) financialSummary.endingNav = currentTotal;
        financialSummary.netChange = change;
      }
    }
    
    // Parse Mark-to-Market Performance Summary
    if (currentSection.includes('mark-to-market') && parts[1] === 'Data') {
      const assetClass = parts[2] || '';
      const priorQuantity = parseFloat(parts[3]) || 0;
      const currentQuantity = parseFloat(parts[4]) || 0;
      const priorPrice = parseFloat(parts[5]) || 0;
      const currentPrice = parseFloat(parts[6]) || 0;
      const mtmPL = parseFloat(parts[7]) || 0;
      const realizedSTPL = parseFloat(parts[8]) || 0;
      const realizedLTPL = parseFloat(parts[9]) || 0;
      const totalPL = parseFloat(parts[10]) || 0;
      
      if (assetClass === 'Total') {
        financialSummary.realizedPL = realizedSTPL + realizedLTPL;
      }
    }
    
    // Parse Open Positions - Multiple format support
    if ((currentSection === 'open positions' || currentSection.includes('positions')) && 
        parts[1] === 'Data') {
      
      // Format 1: Data,Summary,AssetClass,Symbol,Description,Qty,CostPrice,CostBasis,ClosePrice,Value,UnrealizedP/L,Currency
      if (parts[2] === 'Summary' || parts[2] === 'Subtotal') {
        const assetClass = parts[3] || 'Stocks';
        const ticker = parts[4] || '';
        const description = parts[5] || '';
        const qty = parseFloat(parts[6]) || 0;
        const costPrice = parseFloat(parts[7]) || 0;
        const costBasis = parseFloat(parts[8]) || 0;
        const closePrice = parseFloat(parts[9]) || 0;
        const value = parseFloat(parts[10]) || 0;
        const unrealizedPL = parseFloat(parts[11]) || 0;
        const curr = parts[12] || currency;
        
        if (ticker && assetClass !== 'Total' && qty !== 0) {
          positions.push({
            ticker,
            description,
            sector: '',
            quantity: qty,
            marketValue: value || (qty * closePrice),
            costBasis: costBasis || (qty * costPrice),
            unrealizedPL,
            unrealizedPLPercent: costBasis !== 0 ? (unrealizedPL / Math.abs(costBasis)) * 100 : 0,
            currentPrice: closePrice,
            avgCost: costPrice,
            currency: curr,
            assetClass,
          });
        }
      }
      
      // Format 2: Data,Currency,Symbol,Qty,CostPrice,CostBasis,ClosePrice,Value,UnrealizedP/L (Direct format)
      else if (parts[2] && !parts[2].includes('Total') && !parts[2].includes('Subtotal')) {
        const curr = parts[2] || currency;
        const ticker = parts[3] || '';
        const description = parts[4] || '';
        let qty = 0, costPrice = 0, costBasis = 0, closePrice = 0, value = 0, unrealizedPL = 0;
        
        // Try to find numeric columns
        for (let j = 4; j < parts.length; j++) {
          const num = parseFloat(parts[j]);
          if (!isNaN(num)) {
            if (qty === 0 && Math.abs(num) < 1000000) qty = num;
            else if (costPrice === 0) costPrice = num;
            else if (costBasis === 0) costBasis = num;
            else if (closePrice === 0) closePrice = num;
            else if (value === 0) value = num;
            else if (unrealizedPL === 0) unrealizedPL = num;
          }
        }
        
        if (ticker && qty !== 0) {
          const existingPos = positions.find(p => p.ticker === ticker && p.currency === curr);
          if (!existingPos) {
            positions.push({
              ticker,
              description,
              sector: '',
              quantity: qty,
              marketValue: value || (qty * closePrice),
              costBasis: costBasis || (qty * costPrice),
              unrealizedPL: unrealizedPL || (value - costBasis),
              unrealizedPLPercent: costBasis !== 0 ? ((value - costBasis) / Math.abs(costBasis)) * 100 : 0,
              currentPrice: closePrice || (qty !== 0 ? value / qty : 0),
              avgCost: costPrice || (qty !== 0 ? costBasis / qty : 0),
              currency: curr,
              assetClass: 'Stocks',
            });
          }
        }
      }
    }
    
    // Alternative: Long Stock Positions / Short Stock Positions sections
    if ((currentSection === 'long stock positions' || 
         currentSection === 'short stock positions' ||
         currentSection === 'long open positions' ||
         currentSection === 'short open positions') && 
        parts[1] === 'Data') {
      const curr = parts[2] || currency;
      const ticker = parts[3] || '';
      const qty = parseFloat(parts[4]) || 0;
      const costPrice = parseFloat(parts[5]) || 0;
      const costBasis = parseFloat(parts[6]) || 0;
      const closePrice = parseFloat(parts[7]) || 0;
      const value = parseFloat(parts[8]) || 0;
      const unrealizedPL = parseFloat(parts[9]) || 0;
      
      if (ticker && qty !== 0 && ticker !== 'Total') {
        const existingPos = positions.find(p => p.ticker === ticker);
        if (!existingPos) {
          positions.push({
            ticker,
            description: '',
            sector: '',
            quantity: currentSection.includes('short') ? -Math.abs(qty) : Math.abs(qty),
            marketValue: value || (qty * closePrice),
            costBasis: costBasis || (qty * costPrice),
            unrealizedPL,
            unrealizedPLPercent: costBasis !== 0 ? (unrealizedPL / Math.abs(costBasis)) * 100 : 0,
            currentPrice: closePrice,
            avgCost: costPrice,
            currency: curr,
            assetClass: currentSection.includes('short') ? 'Short' : 'Long',
          });
        }
      }
    }
    
    // Parse Trades section - Multiple formats
    if (currentSection === 'trades' && parts[1] === 'Data') {
      // Skip header and subtotal rows
      if (parts[2] === 'Order' || parts[2] === 'Execution') {
        const tradeType = parts[2];
        const assetClass = parts[3] || 'Stocks';
        const curr = parts[4] || currency;
        const ticker = parts[5] || '';
        const dateTime = parts[6] || '';
        const qty = parseFloat(parts[7]) || 0;
        const price = parseFloat(parts[8]) || 0;
        const proceeds = parseFloat(parts[9]) || 0;
        const commission = parseFloat(parts[10]) || parseFloat(parts[11]) || 0;
        const basis = parseFloat(parts[11]) || parseFloat(parts[12]) || 0;
        const realizedPL = parseFloat(parts[12]) || parseFloat(parts[13]) || 0;
        
        if (ticker && qty !== 0) {
          trades.push({
            date: dateTime.split(',')[0] || dateTime.split(' ')[0] || dateTime,
            ticker,
            description: assetClass,
            action: qty > 0 ? 'BUY' : 'SELL',
            quantity: Math.abs(qty),
            price: Math.abs(price),
            proceeds: Math.abs(proceeds),
            commission: Math.abs(commission),
            currency: curr,
            realizedPL: realizedPL,
          });
          
          if (realizedPL !== 0) {
            financialSummary.realizedPL += realizedPL;
          }
        }
      }
      // Alternative simpler format
      else if (parts[2] && parts[3] && !parts[2].includes('Total')) {
        const curr = parts[2] || currency;
        const ticker = parts[3] || '';
        const dateTime = parts[4] || '';
        const qty = parseFloat(parts[5]) || 0;
        const price = parseFloat(parts[6]) || 0;
        const proceeds = parseFloat(parts[7]) || 0;
        const commission = parseFloat(parts[8]) || 0;
        const realizedPL = parseFloat(parts[9]) || 0;
        
        if (ticker && qty !== 0) {
          trades.push({
            date: dateTime.split(',')[0] || dateTime.split(' ')[0] || dateTime,
            ticker,
            description: '',
            action: qty > 0 ? 'BUY' : 'SELL',
            quantity: Math.abs(qty),
            price: Math.abs(price),
            proceeds: Math.abs(proceeds),
            commission: Math.abs(commission),
            currency: curr,
            realizedPL,
          });
        }
      }
    }
    
    // Parse Dividends section
    if (currentSection === 'dividends' && parts[1] === 'Data') {
      const curr = parts[2] || currency;
      const date = parts[3] || '';
      const description = parts[4] || '';
      const amount = parseFloat(parts[5]) || 0;
      
      // Extract ticker from description (usually format: "TICKER(...)...")
      const tickerMatch = description.match(/^([A-Z]+)/);
      const ticker = tickerMatch ? tickerMatch[1] : '';
      
      if (amount !== 0) {
        dividends.push({
          date,
          ticker,
          description,
          amount,
          currency: curr,
        });
        
        if (amount > 0) {
          financialSummary.dividendsReceived += amount;
        }
      }
    }
    
    // Parse Withholding Tax (negative dividend effect)
    if (currentSection === 'withholding tax' && parts[1] === 'Data') {
      const curr = parts[2] || currency;
      const date = parts[3] || '';
      const description = parts[4] || '';
      const amount = parseFloat(parts[5]) || 0;
      
      const tickerMatch = description.match(/^([A-Z]+)/);
      const ticker = tickerMatch ? tickerMatch[1] : '';
      
      if (amount !== 0) {
        dividends.push({
          date,
          ticker,
          description: `Tax: ${description}`,
          amount,
          currency: curr,
        });
      }
    }
    
    // Parse Interest section
    if (currentSection === 'interest' && parts[1] === 'Data') {
      const curr = parts[2] || currency;
      const date = parts[3] || '';
      const description = parts[4] || '';
      const amount = parseFloat(parts[5]) || 0;
      
      if (amount !== 0) {
        cashTransactions.push({
          date,
          description,
          amount,
          currency: curr,
          type: 'interest',
        });
        
        financialSummary.interestReceived += amount;
      }
    }
    
    // Parse Fees section
    if ((currentSection === 'fees' || currentSection === 'commissions') && parts[1] === 'Data') {
      const curr = parts[2] || currency;
      const date = parts[3] || '';
      const description = parts[4] || '';
      const amount = parseFloat(parts[5]) || 0;
      
      if (amount !== 0) {
        cashTransactions.push({
          date,
          description,
          amount,
          currency: curr,
          type: 'fee',
        });
        
        financialSummary.feesAndCommissions += Math.abs(amount);
      }
    }
    
    // Parse Deposits & Withdrawals
    if (currentSection === 'deposits & withdrawals' && parts[1] === 'Data') {
      const curr = parts[2] || currency;
      const date = parts[3] || '';
      const description = parts[4] || '';
      const amount = parseFloat(parts[5]) || 0;
      
      if (amount !== 0) {
        cashTransactions.push({
          date,
          description,
          amount,
          currency: curr,
          type: amount > 0 ? 'deposit' : 'withdrawal',
        });
        
        if (amount > 0) {
          financialSummary.deposits += amount;
        } else {
          financialSummary.withdrawals += Math.abs(amount);
        }
      }
    }
    
    // Parse Cash Report for balance
    if (currentSection === 'cash report' && parts[1] === 'Data') {
      const curr = parts[2] || currency;
      const field = parts[3]?.toLowerCase() || '';
      const amount = parseFloat(parts[4]) || 0;
      
      if (field.includes('ending cash') || field.includes('ending settled cash')) {
        cashBalance = amount;
        if (curr && curr !== 'Total' && curr !== 'Base Currency') {
          cashBalances.push({ currency: curr, balance: amount });
        }
      }
      if (field.includes('starting cash') || field.includes('starting settled cash')) {
        if (financialSummary.beginningNav === 0) {
          financialSummary.beginningNav = amount;
        }
      }
    }
    
    // Parse Financial Instrument Information for better descriptions
    if (currentSection === 'financial instrument information' && parts[1] === 'Data') {
      const assetClass = parts[2] || '';
      const ticker = parts[3] || '';
      const description = parts[4] || '';
      const conid = parts[5] || '';
      const securityId = parts[6] || '';
      const multiplier = parts[7] || '';
      const expiry = parts[8] || '';
      const type = parts[9] || '';
      
      // Update position description if we have it
      const pos = positions.find(p => p.ticker === ticker);
      if (pos && description) {
        pos.description = description;
      }
    }
  }

  // Calculate unrealized P/L for summary
  financialSummary.unrealizedPL = positions.reduce((sum, p) => sum + p.unrealizedPL, 0);

  return {
    format: 'activity_statement',
    positions,
    trades,
    dividends,
    cashTransactions,
    cashBalance,
    cashBalances,
    currency,
    accountId,
    accountInfo,
    statementPeriod,
    financialSummary,
  };
}

// Parse Trade Confirmation format
export function parseTradeConfirmation(csvText: string): ParseResult {
  const lines = csvText.split('\n');
  const trades: Trade[] = [];
  const positions: Position[] = [];
  let accountId = '';
  
  let currentSection = '';
  
  for (const line of lines) {
    const parts = line.split(',').map(p => p.replace(/"/g, '').trim());
    
    if (parts[0] === 'Statement' && parts[1] === 'Header') {
      accountId = parts[4] || '';
    }
    
    if (parts[1] === 'Header') {
      currentSection = parts[0].toLowerCase();
    }
    
    // Parse Trades
    if ((currentSection === 'trades' || currentSection === 'trade confirmations') && 
        parts[1] === 'Data') {
      // Different formats have trades in different column positions
      let ticker = '', date = '', qty = 0, price = 0, commission = 0, curr = 'USD';
      
      // Try to find the data in common positions
      if (parts[2] === 'Stocks' || parts[2] === 'Equity and Index Options') {
        curr = parts[3] || 'USD';
        ticker = parts[4] || '';
        date = parts[5] || '';
        qty = parseFloat(parts[6]) || 0;
        price = parseFloat(parts[7]) || 0;
        commission = parseFloat(parts[10]) || 0;
      } else {
        // Alternative format
        curr = parts[2] || 'USD';
        ticker = parts[3] || '';
        date = parts[4] || '';
        qty = parseFloat(parts[5]) || 0;
        price = parseFloat(parts[6]) || 0;
        commission = parseFloat(parts[9]) || 0;
      }
      
      if (ticker && qty !== 0) {
        trades.push({
          date,
          ticker,
          description: '',
          action: qty > 0 ? 'BUY' : 'SELL',
          quantity: Math.abs(qty),
          price,
          proceeds: Math.abs(qty * price),
          commission: Math.abs(commission),
          currency: curr,
        });
      }
    }
  }
  
  // Convert trades to positions (aggregate by ticker)
  const positionMap = new Map<string, { qty: number; totalCost: number; currency: string }>();
  
  for (const trade of trades) {
    const existing = positionMap.get(trade.ticker);
    if (existing) {
      if (trade.action === 'BUY') {
        existing.qty += trade.quantity;
        existing.totalCost += trade.quantity * trade.price;
      } else {
        existing.qty -= trade.quantity;
        existing.totalCost -= trade.quantity * trade.price;
      }
    } else {
      positionMap.set(trade.ticker, {
        qty: trade.action === 'BUY' ? trade.quantity : -trade.quantity,
        totalCost: trade.action === 'BUY' ? trade.quantity * trade.price : -trade.quantity * trade.price,
        currency: trade.currency,
      });
    }
  }
  
  for (const [ticker, data] of positionMap) {
    if (data.qty !== 0) {
      positions.push({
        ticker,
        description: '',
        sector: '',
        quantity: data.qty,
        marketValue: 0, // Would need current price
        costBasis: data.totalCost,
        unrealizedPL: 0,
        unrealizedPLPercent: 0,
        currentPrice: 0,
        avgCost: data.qty !== 0 ? data.totalCost / data.qty : 0,
        currency: data.currency,
      });
    }
  }

  return {
    format: 'trade_confirmation',
    positions,
    trades,
    dividends: [],
    cashTransactions: [],
    cashBalance: 0,
    cashBalances: [],
    currency: 'USD',
    accountId,
  };
}

// Parse Flex Query format (custom reports)
export function parseFlexQuery(csvText: string): ParseResult {
  const lines = csvText.split('\n');
  const positions: Position[] = [];
  const trades: Trade[] = [];
  let accountId = '';
  
  // Flex queries can have custom column orders, so we need to parse the header
  let headerMap: Map<string, number> = new Map();
  let currentSection = '';
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const parts = line.split(',').map(p => p.replace(/"/g, '').trim());
    
    // Detect headers
    if (parts[1] === 'Header') {
      currentSection = parts[0].toLowerCase();
      headerMap = new Map();
      parts.forEach((col, idx) => {
        headerMap.set(col.toLowerCase(), idx);
      });
      continue;
    }
    
    if (parts[1] !== 'Data') continue;
    
    // Parse positions from various section names
    if (currentSection.includes('position') || currentSection.includes('open')) {
      const ticker = getColumnValue(parts, headerMap, ['symbol', 'ticker', 'underlying']) || '';
      const quantity = parseFloat(getColumnValue(parts, headerMap, ['quantity', 'position', 'qty']) || '0');
      const marketValue = parseFloat(getColumnValue(parts, headerMap, ['marketvalue', 'market value', 'value', 'fifopnlunrealized']) || '0');
      const costBasis = parseFloat(getColumnValue(parts, headerMap, ['costbasis', 'cost basis', 'cost']) || '0');
      const closePrice = parseFloat(getColumnValue(parts, headerMap, ['closeprice', 'close price', 'price', 'mark']) || '0');
      const currency = getColumnValue(parts, headerMap, ['currency', 'curr']) || 'USD';
      
      if (ticker && quantity !== 0) {
        const unrealizedPL = marketValue - costBasis;
        positions.push({
          ticker,
          description: getColumnValue(parts, headerMap, ['description', 'desc', 'name']) || '',
          sector: getColumnValue(parts, headerMap, ['assetclass', 'asset class', 'category']) || '',
          quantity,
          marketValue: marketValue || (quantity * closePrice),
          costBasis,
          unrealizedPL,
          unrealizedPLPercent: costBasis !== 0 ? (unrealizedPL / Math.abs(costBasis)) * 100 : 0,
          currentPrice: closePrice || (quantity !== 0 ? marketValue / quantity : 0),
          avgCost: quantity !== 0 ? costBasis / quantity : 0,
          currency,
        });
      }
    }
    
    // Parse trades
    if (currentSection.includes('trade')) {
      const ticker = getColumnValue(parts, headerMap, ['symbol', 'ticker', 'underlying']) || '';
      const quantity = parseFloat(getColumnValue(parts, headerMap, ['quantity', 'qty']) || '0');
      const price = parseFloat(getColumnValue(parts, headerMap, ['tradeprice', 'price', 'execprice']) || '0');
      const date = getColumnValue(parts, headerMap, ['tradedate', 'date', 'datetime']) || '';
      const commission = parseFloat(getColumnValue(parts, headerMap, ['commission', 'ibcommission', 'comm']) || '0');
      const currency = getColumnValue(parts, headerMap, ['currency', 'curr']) || 'USD';
      
      if (ticker && quantity !== 0) {
        trades.push({
          date,
          ticker,
          description: '',
          action: quantity > 0 ? 'BUY' : 'SELL',
          quantity: Math.abs(quantity),
          price,
          proceeds: Math.abs(quantity * price),
          commission: Math.abs(commission),
          currency,
        });
      }
    }
  }
  
  return {
    format: 'flex_query',
    positions,
    trades,
    dividends: [],
    cashTransactions: [],
    cashBalance: 0,
    cashBalances: [],
    currency: 'USD',
    accountId,
  };
}

// Helper to get column value by possible column names
function getColumnValue(parts: string[], headerMap: Map<string, number>, possibleNames: string[]): string {
  for (const name of possibleNames) {
    const idx = headerMap.get(name.toLowerCase());
    if (idx !== undefined && parts[idx]) {
      return parts[idx];
    }
  }
  return '';
}

// Main parsing function that auto-detects format
export function parseIBKRCSV(csvText: string): ParseResult {
  const format = detectIBKRFormat(csvText);
  
  switch (format) {
    case 'open_position_summary':
    case 'portfolio_analyst':
      return parseOpenPositionSummary(csvText);
    case 'activity_statement':
      return parseActivityStatement(csvText);
    case 'trade_confirmation':
      return parseTradeConfirmation(csvText);
    case 'flex_query':
      return parseFlexQuery(csvText);
    default:
      // Try multiple parsers and return the one with most data
      const results = [
        parseOpenPositionSummary(csvText),
        parseActivityStatement(csvText),
        parseTradeConfirmation(csvText),
        parseFlexQuery(csvText),
      ];
      
      // Return result with most positions
      return results.reduce((best, current) => 
        current.positions.length > best.positions.length ? current : best
      );
  }
}

// Get format description for UI display
export function getFormatDescription(format: ParseResult['format']): string {
  switch (format) {
    case 'open_position_summary':
      return 'Portfolio Analyst - Open Position Summary';
    case 'activity_statement':
      return 'Activity Statement';
    case 'trade_confirmation':
      return 'Trade Confirmation';
    case 'portfolio_analyst':
      return 'Portfolio Analyst Report';
    case 'flex_query':
      return 'Flex Query Report';
    default:
      return 'Unknown Format';
  }
}
